import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Badge,
  Collapse,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AccountCircle,
  ShoppingCart,
  Search,
  Clear,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { brandColors } from "../config/brandTheme";

const NavBar = ({
  user,
  handleMenu,
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  categories,
  cartItemCount,
  primaryBlue,
  glassStyles,
  setCartDrawerOpen,
  showSearch = true, // Allow hiding search (useful for cart page)
  userProfile, // Add userProfile prop for Supreme customer status
}) => {
  const [searchExpanded, setSearchExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isUltraPro =
    userProfile?.ultraPlusApproved === true ||
    userProfile?.ultraPlusStatus === "approved";
  const isUltraCustomer = userProfile?.hasPurchasedPackage === true;
  const customerBadgeLabel = isUltraPro
    ? "ultra plus"
    : isUltraCustomer
      ? "ultra"
      : null;

  const toggleSearch = () => {
    setSearchExpanded(!searchExpanded);
    if (searchExpanded && searchQuery) {
      setSearchQuery(""); // Clear search when collapsing
    }
  };

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: brandColors.navy,
          backdropFilter: "blur(12px)",
          borderBottom: `2px solid ${brandColors.gold}`,
          borderRadius: 0,
          boxShadow: "0 4px 24px rgba(11, 28, 72, 0.35)",
        }}
      >
        <Toolbar sx={{ py: 1, px: { xs: 1, sm: 3 } }}>
          {/* Logo + Company Name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              mr: 2,
              cursor: "pointer",
            }}
            onClick={() => window.location.reload()} // Optional: reload page on logo click
          >
            <Box
              component="img"
              src="/infinitywhalelogo.png"
              alt="InfinityWhale Logo"
              sx={{
                height: { xs: 32, sm: 40, md: 48 },
                width: "auto",
                objectFit: "contain",
                transition: "all 0.3s ease",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                mr: { xs: 1, sm: 1.5, md: 2 },
              }}
            />
            <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "baseline", gap: 1 }}>
              <Typography
                variant={isSmallMobile ? "h5" : "h4"}
                component="div"
                sx={{
                  fontWeight: "bold",
                  background: `linear-gradient(45deg, ${brandColors.gold}, ${brandColors.white})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" },
                  transition: "all 0.3s ease",
                }}
              >
                InfinityWhale
              </Typography>
              {customerBadgeLabel && (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                    fontWeight: 700,
                    color: isUltraPro ? "#E6F0FA" : "#D4AF37",
                    backgroundColor: isUltraPro
                      ? "rgba(11, 28, 72, 0.85)"
                      : "rgba(0, 0, 0, 0.2)",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    border: isUltraPro
                      ? "1px solid rgba(144, 202, 249, 0.6)"
                      : "1px solid rgba(255, 215, 0, 0.3)",
                    textShadow: "0 1px 2px rgba(0,0,0,0.9)",
                    letterSpacing: "0.5px",
                    textTransform: "lowercase",
                    backdropFilter: "blur(10px)",
                    marginLeft: { xs: "4px", sm: "8px" },
                  }}
                >
                  {customerBadgeLabel}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Desktop Search and Category */}
          {!isMobile && showSearch && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mr: 2 }}>
              {/* Search Bar */}
              <TextField
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{
                  width: { sm: 200, md: 250, lg: 300 },
                  "& .MuiOutlinedInput-root": {
                    ...glassStyles,
                    bgcolor: "rgba(255, 255, 255, 0.7)",
                    backdropFilter: "blur(15px)",
                    "& fieldset": {
                      borderColor: "rgba(212, 175, 55, 0.45)",
                    },
                    "&:hover fieldset": {
                      borderColor: primaryBlue[400],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: primaryBlue[500],
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: primaryBlue[500] }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        sx={{ color: primaryBlue[500] }}
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Category Filter */}
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  displayEmpty
                  sx={{
                    ...glassStyles,
                    bgcolor: "rgba(255, 255, 255, 0.7)",
                    backdropFilter: "blur(15px)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(212, 175, 55, 0.45)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: primaryBlue[400],
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: primaryBlue[500],
                    },
                  }}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Right side icons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
            }}
          >
            {/* Welcome message - hidden on small screens */}
            {user && (
              <Typography
                variant="body2"
                sx={{
                  color: brandColors.white,
                  fontWeight: 500,
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  display: { xs: "none", lg: "block" },
                  maxWidth: 120,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Welcome, {user.displayName?.split(" ")[0] || "User"}
              </Typography>
            )}

            {/* Mobile Search Toggle */}
            {isMobile && showSearch && (
              <IconButton
                size="large"
                onClick={toggleSearch}
                sx={{
                  ...glassStyles,
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.3)",
                    transform: "scale(1.05)",
                  },
                }}
              >
                <Search sx={{ color: primaryBlue[600] }} />
              </IconButton>
            )}

            {/* Shopping Cart Icon */}
            <IconButton
              size="large"
              onClick={() => setCartDrawerOpen(true)}
              sx={{
                ...glassStyles,
                bgcolor: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.3)",
                  transform: "scale(1.05)",
                },
              }}
            >
              <Badge
                badgeContent={cartItemCount}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    background: "linear-gradient(45deg, #ff6b6b, #ee5a52)",
                    color: "white",
                    fontWeight: "bold",
                    border: "2px solid white",
                    fontSize: { xs: "0.6rem", sm: "0.75rem" },
                    minWidth: { xs: 16, sm: 20 },
                    height: { xs: 16, sm: 20 },
                  },
                }}
              >
                <ShoppingCart
                  sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
                />
              </Badge>
            </IconButton>

            {/* User Avatar/Profile */}
            {user && (
              <IconButton
                size="large"
                onClick={handleMenu}
                sx={{
                  ...glassStyles,
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.3)",
                    transform: "scale(1.05)",
                  },
                  p: { xs: 0.5, sm: 1 },
                }}
              >
                <Avatar
                  src={user.photoURL}
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    border: `2px solid ${primaryBlue[300]}`,
                  }}
                >
                  <AccountCircle
                    sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
                  />
                </Avatar>
              </IconButton>
            )}
          </Box>
        </Toolbar>

        {/* Mobile Search Collapse */}
        {isMobile && showSearch && (
          <Collapse in={searchExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                ...glassStyles,
                bgcolor: "rgba(255, 255, 255, 0.1)",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Mobile Search Bar */}
                <TextField
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  fullWidth
                  autoFocus={searchExpanded}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      ...glassStyles,
                      bgcolor: "rgba(255, 255, 255, 0.8)",
                      backdropFilter: "blur(15px)",
                      "& fieldset": {
                        borderColor: "rgba(212, 175, 55, 0.45)",
                      },
                      "&:hover fieldset": {
                        borderColor: primaryBlue[400],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: primaryBlue[500],
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: primaryBlue[500] }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                          sx={{ color: primaryBlue[500] }}
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Mobile Category Filter */}
                <FormControl size="small" fullWidth>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    displayEmpty
                    sx={{
                      ...glassStyles,
                      bgcolor: "rgba(255, 255, 255, 0.8)",
                      backdropFilter: "blur(15px)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(212, 175, 55, 0.45)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: primaryBlue[400],
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: primaryBlue[500],
                      },
                    }}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Quick Actions */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {user && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: primaryBlue[700],
                        fontWeight: 500,
                      }}
                    >
                      Hi, {user.displayName?.split(" ")[0] || "User"}!
                    </Typography>
                  )}
                  <IconButton
                    size="small"
                    onClick={toggleSearch}
                    sx={{
                      color: primaryBlue[600],
                      bgcolor: "rgba(255, 255, 255, 0.3)",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.5)",
                      },
                    }}
                  >
                    <ExpandLess />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Collapse>
        )}
      </AppBar>
    </>
  );
};

export default NavBar;
